/* Pathway Coffee — collection page controller
   File: assets/pathway-collection.js

   This file renders UI. It does NOT decide prices.

   Every number shown comes from PathwayAppstle.createPricingEngine().resolve()
   in assets/pathway-appstle.js — the same engine the product page uses. That
   is what guarantees the card and the PDP agree for a given product, variant,
   quantity and purchase type.

   The real Shopify price is rendered server-side, so a card is never blank
   and never depends on this file to show a number. While Appstle's bundle
   rules may still be arriving the figure is simply dimmed. Once they land —
   or once we stop waiting — the engine returns a final price, preferring the
   bundle tier, then the selling-plan price, then plain Shopify.

   Products with no usable subscription price are not offered a subscription
   at all: no Subscribe option, no savings, no schedule. Not every product is
   in the Subscribe & Save bundle, and pretending otherwise would show a
   price the store cannot honour.

   Scope: all lookups are inside a [data-pc-collection] root.
*/
(function () {
  'use strict';

  var MODE_SUBSCRIPTION = 'subscription';
  var MODE_ONETIME = 'onetime';

  /* ==================================================================
     Card
     ================================================================== */

  function Card(element, config, page) {
    this.el = element;
    this.config = config;
    this.page = page;

    var dataNode = element.querySelector('[data-pc-card-data]');
    try {
      this.data = dataNode ? JSON.parse(dataNode.textContent) : null;
    } catch (error) {
      this.data = null;
    }

    this.form = element.querySelector('[data-pc-card-form]');

    // Sold-out products render no form at all; nothing to wire up.
    if (!this.form || !this.data || !Array.isArray(this.data.variants) || !this.data.variants.length) {
      this.inert = true;
      return;
    }

    /*
     * Per-product plan ids (pathway.first_week_plan_id / third_week_plan_id
     * metafields), exactly as the product page passes them. Without them the
     * card could only find the monthly plans by name, and a product whose
     * plan names differ would lose Subscribe & Save on the card while it
     * still works on the product page.
     */
    var planIds = this.data.cyclePlanIds || {};
    if (config.cycleConfig && (planIds.firstWeek || planIds.thirdWeek)) {
      var cycleConfig = Object.assign({}, config.cycleConfig);
      if (cycleConfig.firstWeek) cycleConfig.firstWeek = Object.assign({}, cycleConfig.firstWeek, { planId: planIds.firstWeek || '' });
      if (cycleConfig.thirdWeek) cycleConfig.thirdWeek = Object.assign({}, cycleConfig.thirdWeek, { planId: planIds.thirdWeek || '' });
      config = Object.assign({}, config, { cycleConfig: cycleConfig });
      this.config = config;
    }

    this.engine = window.PathwayAppstle.createPricingEngine(this.data, config);

    this.variantInput = element.querySelector('[data-pc-variant-input]');
    this.quantityInput = element.querySelector('[data-pc-quantity-input]');
    this.planInput = element.querySelector('[data-pc-selling-plan-input]');
    this.cycleWindowInput = element.querySelector('[data-pc-cycle-window-input]');
    this.cycleKeyInput = element.querySelector('[data-pc-cycle-key-input]');
    this.prelaunchMarkerInput = element.querySelector('[data-pc-prelaunch-marker-input]');
    this.prelaunchShipInput = element.querySelector('[data-pc-prelaunch-ship-date-input]');
    this.prelaunchBillingInput = element.querySelector('[data-pc-prelaunch-billing-date-input]');

    // Inventory-aware initial fulfillment: mirrors the product page's inputs.
    this.initialStatusInput = element.querySelector('[data-pc-initial-status-input]');
    this.initialTrackedInput = element.querySelector('[data-pc-initial-tracked-input]');
    this.initialQuantityInput = element.querySelector('[data-pc-initial-quantity-input]');
    this.initialRequestedInput = element.querySelector('[data-pc-initial-requested-input]');
    this.initialExistingCartInput = element.querySelector('[data-pc-initial-existing-cart-input]');
    this.initialReviewInput = element.querySelector('[data-pc-initial-review-input]');
    this.estimatedBillingInput = element.querySelector('[data-pc-estimated-billing-input]');
    this.estimatedShipmentInput = element.querySelector('[data-pc-estimated-shipment-input]');

    // Customer-visible schedule properties: mirrors the product page's
    // Ships / Next Billing / Schedule Details inputs.
    this.checkoutShipsInput = element.querySelector('[data-pc-checkout-ships-input]');
    this.checkoutBillingInput = element.querySelector('[data-pc-checkout-billing-input]');
    this.checkoutDetailsInput = element.querySelector('[data-pc-checkout-details-input]');

    this.pricingEl = element.querySelector('[data-pc-pricing]');
    this.priceEl = element.querySelector('[data-pc-price]');
    this.wasEl = element.querySelector('[data-pc-price-was]');
    this.savingsEl = element.querySelector('[data-pc-savings]');
    this.hintEl = element.querySelector('[data-pc-tier-hint]');
    this.cycleEl = element.querySelector('[data-pc-cycle]');
    this.messageEl = element.querySelector('[data-pc-card-message]');

    this.addButton = element.querySelector('[data-pc-add-button]');
    this.addLabel = element.querySelector('[data-pc-add-label]');

    this.modeField = element.querySelector('[data-pc-mode-field]');
    this.modeSelect = element.querySelector('[data-pc-mode-select]');
    this.onetimeOnlyEl = element.querySelector('[data-pc-onetime-only]');
    this.optionSelects = Array.prototype.slice.call(
      element.querySelectorAll('[data-pc-option-select]')
    );
    this.qtyButtons = Array.prototype.slice.call(
      element.querySelectorAll('[data-pc-qty-change]')
    );
    this.qtyValueEl = element.querySelector('[data-pc-qty-value]');

    this.quantity = 1;
    this.selectedOptions = {};
    this.modeOverride = null; // set only when the customer uses this card's toggle

    var selected = this.data.variants.filter(function (variant) {
      return String(variant.id) === String(this.data.selectedVariantId);
    }, this)[0] || this.data.variants[0];

    if (selected && Array.isArray(selected.options)) {
      selected.options.forEach(function (value, index) {
        this.selectedOptions[index + 1] = value;
      }, this);
    }

    this.bind();
  }

  /** Card mode = its own override if the customer set one, else the page default. */
  Card.prototype.mode = function () {
    return this.modeOverride || this.page.mode();
  };

  Card.prototype.bind = function () {
    var self = this;

    this.optionSelects.forEach(function (select) {
      select.addEventListener('change', function () {
        self.selectedOptions[Number(select.dataset.optionPosition)] = select.value;
        // Appstle rules can be variant-specific, so drop the cached rule.
        self.engine.invalidate();
        self.render();
      });
    });

    this.qtyButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        self.quantity = Math.max(1, self.quantity + (Number(button.dataset.pcQtyChange) || 0));
        self.render();
      });
    });

    if (this.modeSelect) {
      this.modeSelect.addEventListener('change', function () {
        self.modeOverride = self.modeSelect.value === MODE_ONETIME ? MODE_ONETIME : MODE_SUBSCRIPTION;
        self.render();
      });
    }

    this.form.addEventListener('submit', function (event) {
      /*
       * Re-resolve before submitting. The billing cycle is chosen by how far
       * the next billing date sits from today, so a page left open across
       * midnight — or across the 21-day minimum boundary — could otherwise
       * post the cycle that was correct when the page loaded rather than the
       * one that is correct now. Recomputing here costs nothing and keeps
       * the cart line honest.
       */
      try {
        self.render();
      } catch (error) {
        // render() already degrades safely; fall through to the checks below.
      }

      var resolved = self.lastResolved;

      /*
       * Hard stop: never let a customer add to cart unless the engine
       * produced a real price for exactly this configuration.
       */
      if (resolved && resolved.ready && !resolved.available) {
        event.preventDefault();
        self.setMessage('Please choose an available option.', true);
        return;
      }

      if (resolved && !resolved.ready) {
        event.preventDefault();
        self.setMessage('Pricing is still loading. One moment.', true);
        return;
      }

      /*
       * No resolved state at all means the controller failed for this card.
       * The form still carries a valid variant and quantity from Liquid, so
       * let the ordinary Shopify add-to-cart proceed rather than blocking a
       * sale we could have made.
       */

      if (self.addButton) {
        self.addButton.classList.add('is-loading');
        self.addButton.setAttribute('aria-busy', 'true');
      }
      if (self.addLabel) self.addLabel.textContent = 'Adding…';
    });
  };

  Card.prototype.setMessage = function (text, isError) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.hidden = !text;
    this.messageEl.classList.toggle('is-error', Boolean(isError));
  };

  Card.prototype.updateOptionAvailability = function () {
    var api = window.PathwayAppstle;

    this.optionSelects.forEach(function (select) {
      var position = Number(select.dataset.optionPosition);

      Array.prototype.forEach.call(select.options, function (option) {
        var available = api.isOptionValueAvailable(
          this.data.variants,
          this.selectedOptions,
          position,
          option.value
        );

        option.disabled = !available;
        var base = option.textContent.replace(/\s*—\s*sold out$/i, '');
        option.textContent = available ? base : base + ' — sold out';
      }, this);
    }, this);
  };

  /**
   * Guarded entry point. A single malformed product must not be able to throw
   * partway through and leave the rest of the grid unrendered, so failures
   * are contained to the card that caused them. That card keeps its
   * server-rendered Shopify price and stays purchasable.
   */
  Card.prototype.render = function () {
    try {
      this.renderInternal();
    } catch (error) {
      this.degrade(error);
    }
  };

  Card.prototype.degrade = function (error) {
    if (window.console && console.warn) {
      console.warn('[pathway-collection] card failed to render, falling back to one-time', {
        productId: this.data && this.data.productId,
        error: error
      });
    }

    // Strip anything subscription-specific; the Shopify price stays as-is.
    if (this.pricingEl) this.pricingEl.classList.remove('is-pending');
    if (this.modeField) this.modeField.hidden = true;
    if (this.onetimeOnlyEl) this.onetimeOnlyEl.hidden = false;
    if (this.cycleEl) this.cycleEl.hidden = true;
    if (this.savingsEl) this.savingsEl.hidden = true;
    if (this.hintEl) this.hintEl.hidden = true;
    if (this.wasEl) this.wasEl.hidden = true;
    if (this.planInput) {
      this.planInput.value = '';
      this.planInput.disabled = true;
    }
    if (this.addButton) this.addButton.disabled = false;
  };

  Card.prototype.renderInternal = function () {
    if (this.inert) return;

    var api = window.PathwayAppstle;
    var labels = this.config.labels || {};
    var mode = this.mode();

    var settled = this.page.appstleSettled();

    var resolved = this.engine.resolve({
      selectedOptions: this.selectedOptions,
      quantity: this.quantity,
      mode: mode,
      settled: settled
    });

    /*
     * The engine reports ineligible products as not subscribable and already
     * prices them one-time. Reflect that in the card's own mode so the
     * schedule line and savings stay hidden.
     */
    if (resolved.fellBackToOneTime) mode = MODE_ONETIME;

    this.lastResolved = resolved;

    /* --- Clamp quantity to the tiers the merchant configured --- */
    if (resolved.bounds) {
      var clamped = Math.min(resolved.bounds.max, Math.max(resolved.bounds.min, this.quantity));
      if (clamped !== this.quantity) {
        this.quantity = clamped;
        resolved = this.engine.resolve({
          selectedOptions: this.selectedOptions,
          quantity: this.quantity,
          mode: mode,
          settled: settled
        });
        this.lastResolved = resolved;
      }
    }

    this.updateOptionAvailability();

    /*
     * Whether this product can be bought as a subscription at all is a fact
     * about the product and selected variant, not about the mode currently
     * chosen. A one-time resolve never reports eligibility, so ask the engine
     * separately; otherwise choosing One-Time would hide the selector and
     * leave the customer no way back to Subscribe & Save.
     */
    var subscriptionEligible = resolved.subscriptionEligible;
    if (mode === MODE_ONETIME) {
      subscriptionEligible = this.engine.resolve({
        selectedOptions: this.selectedOptions,
        quantity: this.quantity,
        mode: MODE_SUBSCRIPTION,
        settled: settled
      }).subscriptionEligible;
    }

    if (this.qtyValueEl) this.qtyValueEl.textContent = String(this.quantity);
    this.qtyButtons.forEach(function (button) {
      var delta = Number(button.dataset.pcQtyChange);
      var bounds = resolved.bounds || { min: 1, max: 12 };
      button.disabled = delta < 0 ? this.quantity <= bounds.min : this.quantity >= bounds.max;
    }, this);

    /*
     * Purchase type is only offered when the engine confirmed a real selling
     * plan. Ineligible products never see it and stay one-time, even while
     * the page-level toggle says Subscribe.
     */
    if (this.modeField) this.modeField.hidden = !subscriptionEligible;
    if (this.modeSelect && this.modeSelect.value !== mode) this.modeSelect.value = mode;

    /*
     * Ineligible products get an explicit "one-time purchase only" line where
     * the selector would be, so the absence reads as a deliberate fact about
     * the product rather than a control that failed to appear.
     */
    if (this.onetimeOnlyEl) this.onetimeOnlyEl.hidden = subscriptionEligible;

    /* --- Cart payload: always the configuration we just priced --- */
    if (this.variantInput && resolved.variantId) this.variantInput.value = resolved.variantId;
    if (this.quantityInput) this.quantityInput.value = resolved.quantity;

    this.syncSubscriptionFields(resolved, mode);
    this.syncFulfillmentFields(resolved, mode);

    this.renderPricing(resolved, labels, api);
    this.renderSchedule(resolved, labels, api);
    this.renderButton(resolved, labels);
  };

  /**
   * Writes the subscription half of the cart payload: selling plan plus the
   * Fresh Roast cycle and prelaunch line-item properties. Mirrors syncForm()
   * on the product page so both entry points build the same cart line.
   *
   * Everything is disabled unless a subscription is genuinely being bought;
   * a disabled input is not submitted, so a one-time purchase carries no
   * subscription metadata at all.
   */
  Card.prototype.syncSubscriptionFields = function (resolved, mode) {
    var prelaunch = this.config.prelaunch || {};
    var cycle = resolved.cycle;
    var isSubscription =
      mode === MODE_SUBSCRIPTION &&
      resolved.ready &&
      resolved.subscriptionEligible &&
      Boolean(resolved.sellingPlanId) &&
      Boolean(cycle);

    function set(input, value) {
      if (!input) return;
      if (value == null || value === '') {
        input.value = '';
        input.disabled = true;
      } else {
        input.value = value;
        input.disabled = false;
      }
    }

    if (!isSubscription) {
      set(this.planInput, null);
      set(this.cycleWindowInput, null);
      set(this.cycleKeyInput, null);

      /*
       * One-time purchase. Same rule as syncForm() on the product page: while
       * a valid Prelaunch Mode is active a one-time line still carries the
       * prelaunch marker and the configured ship date (never a billing date —
       * a one-time order does not recur), so the cart's prelaunch messaging
       * and order logic treat it the same wherever it was added from.
       */
      var onetimeIsPrelaunch =
        mode !== MODE_SUBSCRIPTION &&
        Boolean(prelaunch.enabled) &&
        window.PathwayAppstle.isPrelaunchConfigValid(prelaunch);

      set(this.prelaunchMarkerInput, onetimeIsPrelaunch ? 'true' : null);
      set(this.prelaunchShipInput, onetimeIsPrelaunch ? prelaunch.shipDate : null);
      set(this.prelaunchBillingInput, null);
      return;
    }

    set(this.planInput, resolved.sellingPlanId);
    set(this.cycleWindowInput, cycle.window);
    set(this.cycleKeyInput, cycle.key);

    // Prelaunch markers only when this really is the forced prelaunch cycle.
    if (cycle.isPrelaunch) {
      set(this.prelaunchMarkerInput, 'true');
      set(this.prelaunchShipInput, prelaunch.shipDate);
      set(this.prelaunchBillingInput, prelaunch.nextBillingDate);
    } else {
      set(this.prelaunchMarkerInput, null);
      set(this.prelaunchShipInput, null);
      set(this.prelaunchBillingInput, null);
    }
  };

  /**
   * Writes the inventory-aware initial-fulfillment metadata, mirroring
   * syncForm()'s equivalent block on the product page. Unlike
   * syncSubscriptionFields(), this always runs — for one-time purchases too —
   * because a direct add-to-cart from this card must never leave an order
   * unmarked. Only the two date/shipment inputs are subscription-only.
   *
   * This does not decide anything about pricing or eligibility; it only
   * records, on the order, whether inventory covered the requested quantity
   * at the moment of purchase. No Appstle API is called and no fulfillment
   * hold is created here — see the comment above initialFulfillmentStatus()
   * for what a later Shopify Flow may do with this data.
   */
  Card.prototype.syncFulfillmentFields = function (resolved, mode) {
    var api = window.PathwayAppstle;

    function set(input, value) {
      if (!input) return;
      if (value == null || value === '') {
        input.value = '';
        input.disabled = true;
      } else {
        input.value = value;
        input.disabled = false;
      }
    }

    if (!resolved.variant) {
      // No resolvable variant (malformed data) - leave every field disabled
      // rather than submit a guess.
      set(this.initialStatusInput, null);
      set(this.initialTrackedInput, null);
      set(this.initialQuantityInput, null);
      set(this.initialRequestedInput, null);
      set(this.initialExistingCartInput, null);
      set(this.initialReviewInput, null);
      set(this.estimatedBillingInput, null);
      set(this.estimatedShipmentInput, null);
      set(this.checkoutShipsInput, null);
      set(this.checkoutBillingInput, null);
      set(this.checkoutDetailsInput, null);
      return;
    }

    var existingCartQuantity = this.page.existingCartQuantity(resolved.variant.id);
    var cartKnown = this.page.cartSnapshotKnown();
    var tracked = Boolean(resolved.variant.inventoryManagement);

    var inventoryStatusResult;
    if (tracked && !cartKnown) {
      // Same rule as the product page: never claim "sufficient" before we
      // actually know what else of this variant is already in the cart.
      var rawQuantity = resolved.variant.inventoryQuantity;
      var quantityIsValid = typeof rawQuantity === 'number' && isFinite(rawQuantity);
      var requested = api.normalizeNonNegativeInt(resolved.quantity);

      inventoryStatusResult = {
        tracked: true,
        inventoryQuantity: quantityIsValid ? rawQuantity : null,
        requestedQuantity: requested,
        existingCartQuantity: 0,
        totalRequestedQuantity: requested,
        status: 'unknown',
        reviewRequired: true
      };
    } else {
      inventoryStatusResult = api.getInventoryStatus(resolved.variant, resolved.quantity, existingCartQuantity);
    }

    var prelaunchConfig = this.config.prelaunch || {};
    var prelaunchActiveForThisMode =
      mode === MODE_SUBSCRIPTION
        ? Boolean(resolved.cycle && resolved.cycle.isPrelaunch)
        : Boolean(prelaunchConfig.enabled && api.isPrelaunchConfigValid(prelaunchConfig));

    var statusValue;
    var reviewRequiredValue;
    if (prelaunchActiveForThisMode) {
      statusValue = 'prelaunch-scheduled';
      reviewRequiredValue = false;
    } else if (inventoryStatusResult.status === 'sufficient') {
      statusValue = 'available-inventory';
      reviewRequiredValue = false;
    } else if (inventoryStatusResult.status === 'insufficient') {
      statusValue = 'next-roast-review';
      reviewRequiredValue = true;
    } else {
      statusValue = 'inventory-unknown-review';
      reviewRequiredValue = true;
    }

    set(this.initialStatusInput, statusValue);
    set(this.initialTrackedInput, inventoryStatusResult.tracked ? 'true' : 'false');
    set(
      this.initialQuantityInput,
      inventoryStatusResult.inventoryQuantity === null ? '' : String(inventoryStatusResult.inventoryQuantity)
    );
    set(this.initialRequestedInput, String(inventoryStatusResult.requestedQuantity));
    set(this.initialExistingCartInput, String(inventoryStatusResult.existingCartQuantity));
    set(this.initialReviewInput, reviewRequiredValue ? 'true' : 'false');

    if (mode === MODE_SUBSCRIPTION && resolved.cycle) {
      set(this.estimatedBillingInput, api.formatIsoDate(resolved.cycle.renewalDate));
      set(this.estimatedShipmentInput, api.formatShipLabel(resolved.cycle));
    } else {
      set(this.estimatedBillingInput, null);
      set(this.estimatedShipmentInput, null);
    }

    var schedule = this.checkoutSchedule(resolved, mode, inventoryStatusResult.status === 'sufficient');
    set(this.checkoutShipsInput, schedule.ships);
    set(this.checkoutBillingInput, schedule.nextBilling);
    set(this.checkoutDetailsInput, schedule.details);
  };

  /**
   * Customer-visible Ships / Next Billing / Schedule Details values. A port of
   * getCheckoutSchedule() in sections/pathway-product-main.liquid, reading the
   * same Theme settings (passed in via the section config), so a line added
   * from this card shows the same schedule in cart and checkout as one added
   * from the product page.
   */
  Card.prototype.checkoutSchedule = function (resolved, mode, sufficient) {
    var api = window.PathwayAppstle;
    var prelaunch = this.config.prelaunch || {};
    var fulfillment = this.config.fulfillment || {};
    var result = { ships: '', nextBilling: '', details: '' };
    var availableValue = fulfillment.initialShipmentAvailableValue || 'Ships within 1–4 business days';
    var nextRoastValue = fulfillment.initialShipmentNextRoastValue || 'Next available roast batch';
    var prelaunchValid = Boolean(prelaunch.enabled) && api.isPrelaunchConfigValid(prelaunch);

    function longDate(value) {
      var date = api.parseIsoDateString(value);
      return date ? api.formatDate(date) : '';
    }

    // Same "In stock: <message>" plain-text form as the product page.
    function withStockLabel(message) {
      if (!message) return '';
      var label = sufficient ? (fulfillment.labelInStock || '') : (fulfillment.labelNotInStock || '');
      return label ? label + ' ' + message : message;
    }

    function fillDates(text) {
      return String(text || '')
        .replace(/\{\{\s*ship_date\s*\}\}/gi, longDate(prelaunch.shipDate))
        .replace(/\{\{\s*billing_date\s*\}\}/gi, longDate(prelaunch.nextBillingDate));
    }

    if (mode === MODE_SUBSCRIPTION) {
      var cycle = resolved.cycle;
      if (!cycle || !resolved.sellingPlanId || !resolved.subscriptionEligible) return result;

      if (cycle.isPrelaunch) {
        result.ships = api.formatCustomerShipDate(cycle);
        if (prelaunchValid && prelaunch.message) result.details = fillDates(prelaunch.message);
      } else {
        result.ships = sufficient ? availableValue : nextRoastValue;
        result.details = withStockLabel(sufficient
          ? (fulfillment.subscriptionAvailableMessage || '')
          : (fulfillment.subscriptionInsufficientMessage || ''));
      }

      result.nextBilling = api.formatBillingDate(cycle);
    } else if (prelaunchValid) {
      result.ships = longDate(prelaunch.shipDate);
      // One-time has no recurring billing, so only {{ ship_date }} applies.
      result.details = String(prelaunch.onetimeMessage || '')
        .replace(/\{\{\s*ship_date\s*\}\}/gi, longDate(prelaunch.shipDate));
    } else {
      result.ships = sufficient ? availableValue : nextRoastValue;
      result.details = withStockLabel(sufficient
        ? (fulfillment.onetimeAvailableMessage || '')
        : (fulfillment.onetimeInsufficientMessage || ''));
    }

    return result;
  };

  Card.prototype.renderPricing = function (resolved, labels, api) {
    var pricingReady = resolved.ready;

    if (this.pricingEl) this.pricingEl.classList.toggle('is-pending', !pricingReady);

    if (!pricingReady) {
      /*
       * Still waiting on Appstle. Leave the server-rendered Shopify price in
       * place, just dimmed — the customer always sees a real, correct price
       * and is never presented with an empty box.
       */
      if (this.wasEl) this.wasEl.hidden = true;
      if (this.savingsEl) this.savingsEl.hidden = true;
      if (this.hintEl) this.hintEl.hidden = true;
      return;
    }

    var isSubscription = resolved.mode === MODE_SUBSCRIPTION && resolved.subscriptionEligible;

    /*
     * Headline is the per-bag price so the number stays comparable as the
     * customer changes quantity. Appstle tiers change the per-bag rate, so
     * this is the figure that actually moves.
     */
    if (this.priceEl) this.priceEl.textContent = api.money(resolved.unitPrice);

    var savings = isSubscription ? resolved.savings : 0;
    var showSavings = savings > 0.005;

    // Struck-through original only appears when there is a real saving.
    if (this.wasEl) {
      this.wasEl.textContent = showSavings ? api.money(resolved.oneTimeUnit) : '';
      this.wasEl.hidden = !showSavings;
    }

    if (this.savingsEl) {
      this.savingsEl.textContent = showSavings
        ? (labels.subscribeSave || 'Save') + ' ' + api.money(savings)
        : '';
      this.savingsEl.hidden = !showSavings;
    }

    /*
     * Multi-bag messaging. Names a specific bag count and per-bag price only
     * when Appstle actually exposes a cheaper tier. Hidden entirely when it
     * does not — no invented thresholds, no generic filler.
     */
    if (this.hintEl) {
      if (isSubscription && resolved.nextTier) {
        var more = resolved.nextTier.quantity - resolved.quantity;
        this.hintEl.textContent =
          'Add ' + (more === 1 ? '1 more bag' : more + ' more bags') +
          ' for ' + api.money(resolved.nextTier.unit) + ' per bag';
        this.hintEl.hidden = false;
      } else {
        this.hintEl.hidden = true;
      }
    }
  };

  Card.prototype.renderSchedule = function (resolved, labels, api) {
    if (!this.cycleEl) return;

    /*
     * Billing information belongs only to an active, eligible subscription.
     * One-time buyers and non-subscription products never see a billing date.
     */
    if (resolved.mode !== MODE_SUBSCRIPTION || !resolved.subscriptionEligible) {
      this.cycleEl.hidden = true;
      return;
    }

    if (!resolved.ready || !resolved.cycle) {
      this.cycleEl.hidden = true;
      return;
    }

    /*
     * The forced prelaunch cycle's "First shipment · Next billing" line is
     * not shown on the card. The dates are unchanged and still submitted
     * with the cart line (see syncSubscriptionFields / checkoutSchedule), so
     * the cart, checkout and order logic keep receiving them.
     */
    if (resolved.cycle.isPrelaunch) {
      this.cycleEl.textContent = '';
      this.cycleEl.hidden = true;
      return;
    }

    var opts = { month: 'short', day: 'numeric' };
    var ships = api.formatCustomerShipDate(resolved.cycle, opts);
    var bills = api.formatBillingDate(resolved.cycle, opts);
    var parts = [];

    if (ships) {
      parts.push((resolved.cycle.isPrelaunch ? 'First shipment ' : 'Ships ') + ships);
    }
    if (bills) parts.push('Next billing ' + bills);

    this.cycleEl.textContent = parts.join(' · ');
    this.cycleEl.hidden = !parts.length;
  };

  Card.prototype.renderButton = function (resolved, labels) {
    if (!this.addButton) return;

    var enabled = resolved.ready && resolved.available;

    this.addButton.disabled = !enabled;
    this.addButton.classList.remove('is-loading');
    this.addButton.removeAttribute('aria-busy');

    if (!this.addLabel) return;

    if (!resolved.available) {
      this.addLabel.textContent = labels.soldOut || 'Sold Out';
    } else if (resolved.ready) {
      this.addLabel.textContent = labels.addToCart || 'Add to Cart';
      this.setMessage('');
    } else {
      this.addLabel.textContent = labels.preparing || 'Preparing pricing…';
    }
  };

  /* ==================================================================
     Section
     ================================================================== */

  function initSection(root) {
    if (root.dataset.pcCollectionReady === 'true') return;
    root.dataset.pcCollectionReady = 'true';

    if (!window.PathwayAppstle) return; // shared engine failed to load

    var configNode = root.querySelector('[data-pc-collection-config]');
    var config = {};
    try {
      config = configNode ? JSON.parse(configNode.textContent) : {};
    } catch (error) {
      config = {};
    }

    var pageMode = root.dataset.pcDefaultMode === MODE_ONETIME ? MODE_ONETIME : MODE_SUBSCRIPTION;

    /*
     * "Settled" means we have stopped waiting for Appstle. Until then a
     * subscribable product with no tier yet may hold a skeleton; afterwards
     * the engine must return a usable price no matter what.
     */
    var appstleSettled = Boolean(window._ABConfig);

    /*
     * Existing cart quantity, shared by every card on the page. Fetched once
     * (the whole cart, not per-variant) so N cards cost one request, not N.
     * null = not yet known (loading, or the fetch failed) - callers must
     * treat that as "unknown", never as "nothing in cart".
     */
    var cartQuantitiesByVariantId = null;
    var cartFetchStarted = false;

    var page = {
      mode: function () {
        return pageMode;
      },
      appstleSettled: function () {
        return appstleSettled;
      },
      cartSnapshotKnown: function () {
        return cartQuantitiesByVariantId !== null;
      },
      existingCartQuantity: function (variantId) {
        if (!cartQuantitiesByVariantId) return 0;
        return cartQuantitiesByVariantId[String(variantId)] || 0;
      }
    };

    function getCartRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    function fetchCartSnapshot() {
      if (cartFetchStarted) return;
      cartFetchStarted = true;

      fetch(getCartRoot() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart request failed with status ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          var api = window.PathwayAppstle;
          var map = {};
          (Array.isArray(cart && cart.items) ? cart.items : []).forEach(function (item) {
            var id = String(item.variant_id);
            map[id] = (map[id] || 0) + api.normalizeNonNegativeInt(item.quantity);
          });
          cartQuantitiesByVariantId = map;
          renderAll();
        })
        .catch(function () {
          // Leave it null: every card treats a tracked variant as "unknown"
          // (review required) rather than silently assuming zero in cart.
          cartQuantitiesByVariantId = null;
          renderAll();
        });
    }

    /*
     * Construction is guarded per card for the same reason render is: one
     * product with unexpected data must not take the whole grid down with it.
     */
    var cards = [];
    Array.prototype.forEach.call(root.querySelectorAll('[data-pc-card]'), function (element) {
      try {
        cards.push(new Card(element, config, page));
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('[pathway-collection] skipping card that failed to initialise', error);
        }
      }
    });

    function renderAll() {
      cards.forEach(function (card) {
        card.render();
      });
    }

    // Non-blocking: cards render immediately with the server-rendered price
    // and "unknown" inventory status, then re-render once this resolves.
    fetchCartSnapshot();

    /*
     * Registered BEFORE the first render on purpose. If an early card were to
     * throw during the initial pass, a poll registered afterwards would never
     * run and every remaining card would wait for Appstle forever.
     */
    startAppstleWatch();

    /* --- Page-level purchase mode --- */

    var modeButtons = Array.prototype.slice.call(root.querySelectorAll('[data-pc-mode]'));
    var noteSubscription = root.querySelector('[data-pc-mode-note-subscription]');
    var noteOnetime = root.querySelector('[data-pc-mode-note-onetime]');

    function applyPageMode(next) {
      pageMode = next;

      modeButtons.forEach(function (button) {
        var isActive = button.dataset.pcMode === pageMode;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (noteSubscription) noteSubscription.hidden = pageMode !== MODE_SUBSCRIPTION;
      if (noteOnetime) noteOnetime.hidden = pageMode !== MODE_ONETIME;

      /*
       * The page toggle resets every card, clearing per-card overrides —
       * it is an explicit instruction for the whole page. A card toggle,
       * by contrast, only ever changes that one card.
       */
      cards.forEach(function (card) {
        card.modeOverride = null;
        card.render();
      });
    }

    modeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        applyPageMode(button.dataset.pcMode === MODE_ONETIME ? MODE_ONETIME : MODE_SUBSCRIPTION);
      });
    });

    applyPageMode(pageMode);

    /* --- Sort --- */

    var sortForm = root.querySelector('[data-pc-sort-form]');
    if (sortForm) {
      var sortSelect = sortForm.querySelector('select[name="sort_by"]');
      if (sortSelect) {
        sortSelect.addEventListener('change', function () {
          sortForm.submit();
        });
      }
    }

    /*
     * Appstle injects _ABConfig asynchronously via its app embed, and tier
     * pricing is unavailable until it lands. Poll briefly and re-render as
     * soon as it appears; cards show a skeleton until then.
     */
    function startAppstleWatch() {
      if (appstleSettled) return;

      var attempts = 0;
      var poll = window.setInterval(function () {
        attempts += 1;

        var arrived = Boolean(window._ABConfig);

        /*
         * ~2.5s is the ceiling. Settle either way: a card stuck waiting for
         * Appstle is worse than one priced from Shopify, because the former
         * blocks a sale we could have made.
         */
        if (arrived || attempts > 10) {
          window.clearInterval(poll);
          appstleSettled = true;

          if (arrived) {
            cards.forEach(function (card) {
              if (card.engine) card.engine.invalidate();
            });
          }

          renderAll();
        }
      }, 250);
    }
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pc-collection]');
    Array.prototype.forEach.call(roots, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
